import { useEffect, useRef } from 'react'

import {
  Divider,
  SquareDotLoadingIndicator,
  Subtitle,
  typography,
} from '@joor/design-system'
import { uniqueId } from 'lodash'
import styled from 'styled-components/macro'

import { useOnScreen } from 'hooks/screenHooks'

import { MessagesItem } from './MessagesItem'

const Container = styled.div`
  display: flex;
  height: 100%;
  margin: 0;
  padding: 8px;
  background-color: var(--color-neutral-000-full);
  border: 1px solid var(--color-primary-400);
  border-radius: var(--size-border-radius-medium);
  overflow-y: auto;
  scroll-behavior: auto;
  > span {
    ${typography.alpha.primaryActive.body3}
  }
`
const MessagesWrapper = styled.div`
  width: 100%;
`

export const StyledDivider = styled(Divider)`
  margin: 15px 0;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`

const LoaderContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`

const StyledSubtitle = styled(Subtitle)`
  line-height: 16px !important;
  paragraph-spacing: 8px !important;
`

const NoCommentsWrapper = styled.div`
  height: 100%;
  width: 100%;
  border-radius: 10px;
  border: 1px solid black;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 5px;
  gap: 8px;
`

export type OrderMessage = {
  prefix?: string
  message: string
}

type Props = {
  orderComments: OrderMessage[]
  messages: OrderMessage[]
  loading?: boolean
}

export const MessagesList = ({
  messages,
  orderComments,
  loading,
}: Props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isIntersecting = useOnScreen(containerRef)

  const scrollToBottom = (): void => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      ...(isIntersecting && { behavior: 'smooth' }),
    })
  }

  useEffect(scrollToBottom, [messages, isIntersecting])

  if (loading) {
    return (
      <LoaderContainer>
        <SquareDotLoadingIndicator />
      </LoaderContainer>
    )
  }

  if (orderComments.length === 0 && messages.length === 0) {
    return (
      <NoCommentsWrapper>
        <Subtitle type="1">No Comments</Subtitle>
        <StyledSubtitle
          letterSpacing="tight"
          color="primary-400"
          type="3"
          textAlign="center"
          fontSize="extrasmall"
          lineHeight="16"
        >
          This is the beginning of your comment thread. Use the comment box
          below to add a comment.
        </StyledSubtitle>
      </NoCommentsWrapper>
    )
  }

  return (
    <Container ref={containerRef}>
      <MessagesWrapper>
        {!!orderComments.length && (
          <>
            {orderComments.map(({ message }) => (
              <Row>{message}</Row>
            ))}
            <StyledDivider />
          </>
        )}
        {messages.map(({ prefix: sender, message }) => (
          <MessagesItem
            key={`${uniqueId('message-')}`}
            sender={sender}
            content={message}
          />
        ))}
      </MessagesWrapper>
    </Container>
  )
}
